import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { supabase } from '../lib/supabase';
import { formatMessageTime } from '../lib/formatMessageTime';
import Loader from './Loader';
import FileViewerModal from './FileViewerModal';
import useFileViewer from '../lib/UsefileViewer';
import './SubjectPortal.css';

// ── Days-left helper ───────────────────────────────────────
const getDaysLeft = (createdAt) => {
  const expiresAt = new Date(new Date(createdAt).getTime() + 7 * 24 * 60 * 60 * 1000);
  const msLeft    = expiresAt - Date.now();
  return Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
};

const getExpiryColor = (days) => {
  if (days >= 5) return '#06d6a0';
  if (days >= 3) return '#ffd166';
  return '#ef476f';
};

const ExpiryDot = ({ createdAt }) => {
  const days  = getDaysLeft(createdAt);
  const color = getExpiryColor(days);
  return (
    <span
      className="sp-expiry-dot"
      title={days === 0 ? 'Expires today' : `${days} day${days !== 1 ? 's' : ''} left`}
      style={{ background: color }}
    >
      {days}
    </span>
  );
};

// ── Toast ──────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className={`sp-toast sp-toast-${type}`}>
      <span className="sp-toast-icon">
        {type === 'success' ? '✓' : type === 'error' ? '✗' : '⟳'}
      </span>
      <span className="sp-toast-message">{message}</span>
    </div>
  );
};

const SubjectPortal = () => {
  const { subjectId } = useParams();
  const navigate      = useNavigate();

  const [dropdownOpen,  setDropdownOpen]  = useState(false);
  const [fileList,      setFileList]      = useState([]);
  const [subjectName,   setSubjectName]   = useState('');
  const [groupRate,     setGroupRate]     = useState(0);
  const [billingCycle,  setBillingCycle]  = useState('monthly');
  const [participants,  setParticipants]  = useState([]);
  const [onlineUsers,   setOnlineUsers]   = useState([]);
  const [messages,      setMessages]      = useState([]);
  const [newMessage,    setNewMessage]    = useState('');
  const [accessStatus,  setAccessStatus]  = useState(null);
  const [uploading,     setUploading]     = useState(false);
  const [uploadToast,   setUploadToast]   = useState(null);

  // Leave modal state
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const { viewerFile, openFile, closeFile } = useFileViewer();

  const dropdownRef   = useRef(null);
  const plusButtonRef = useRef(null);
  const fileInputRef  = useRef(null);
  const messagesEndRef= useRef(null);
  const textareaRef   = useRef(null);

  const currentUser     = JSON.parse(localStorage.getItem('user'));
  const currentUserId   = currentUser?._id || currentUser?.id;
  const currentUserName = `${currentUser.firstName} ${currentUser.lastName}`;
  const token           = localStorage.getItem('token');
  const authHeaders     = { Authorization: `Bearer ${token}` };

  // ── Auto-expand textarea ──────────────────────────────────
  const handleTextareaChange = (e) => {
    setNewMessage(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [messages]);

  // ── Fetch files ──────────────────────────────────────────
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const res  = await fetch(`https://mykaddle-api.onrender.com/api/groups/${subjectId}/files`, { headers: authHeaders });
        const data = await res.json();
        setFileList(Array.isArray(data) ? data : []);
      } catch (err) { 
        console.error('Failed to load files', err);
        setFileList([]);
      }
    };
    fetchFiles();
  }, [subjectId]);

  // ── Fetch group info ─────────────────────────────────────
  useEffect(() => {
    const fetchGroupInfo = async () => {
      try {
        const res  = await fetch(`https://mykaddle-api.onrender.com/api/groups/${subjectId}/info`, { headers: authHeaders });
        const data = await res.json();
        setSubjectName(data.subjectDisplayName);
        setGroupRate(data.rate || 0);
        setBillingCycle(data.billingCycle || 'monthly');
      } catch (err) { console.error('Failed to load group info', err); }
    };
    fetchGroupInfo();
  }, [subjectId]);

  // ── Fetch members ────────────────────────────────────────
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const res  = await fetch(`https://mykaddle-api.onrender.com/api/groups/${subjectId}/members`, { headers: authHeaders });
        const data = await res.json();
        setParticipants(Array.isArray(data) ? data : []);
      } catch (err) { console.error('Failed to load members', err); }
    };
    fetchMembers();
  }, [subjectId]);

  // ── Socket presence ──────────────────────────────────────
  useEffect(() => {
    const socket = io('https://mykaddle-api.onrender.com');
    const userId = currentUser?._id || currentUser?.id;
    socket.emit('joinGroup', { subjectId, userId });
    socket.on('onlineUsers', (users) => setOnlineUsers(users));
    return () => { socket.emit('leaveGroup', { subjectId, userId }); socket.disconnect(); };
  }, [subjectId]);

  // ── Supabase messages ────────────────────────────────────
  useEffect(() => {
    const fetchMessages = async () => {
      const { data, error } = await supabase
        .from('messages').select('*')
        .eq('subject_id', subjectId)
        .order('created_at', { ascending: true });
      if (!error) setMessages(data || []);
    };
    fetchMessages();
  }, [subjectId]);

  useEffect(() => {
    const channel = supabase
      .channel(`messages-${subjectId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `subject_id=eq.${subjectId}` },
        (payload) => setMessages(prev => [...prev, payload.new]))
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'messages', filter: `subject_id=eq.${subjectId}` },
        (payload) => setMessages(prev => prev.filter(m => m.id !== payload.old.id)))
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [subjectId]);

  // ── Send message ─────────────────────────────────────────
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    if (!currentUserId) {
      console.error('User ID not found. Cannot send message.');
      return;
    }
    const { error } = await supabase.from('messages').insert([{
      subject_id: subjectId,
      sender_id: currentUserId,
      sender_name: currentUserName,
      recipient_id: 'group',
      recipient_name: 'Group Chat',
      content: newMessage.trim()
    }]);
    if (error) { 
      console.error('Send error:', error);
      alert('Failed to send message. Error: ' + error.message);
      return; 
    }
    setNewMessage('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  // ── Delete message ───────────────────────────────────────
  const handleDeleteMessage = async (msg) => {
    if (msg.sender_id !== currentUserId) {
      console.error('You can only delete your own messages');
      return;
    }
    setMessages(prev => prev.filter(m => m.id !== msg.id));
    const { error } = await supabase.from('messages').delete().eq('id', msg.id);
    if (error) {
      console.error('Delete error:', error);
      setMessages(prev => [...prev, msg].sort((a, b) =>
        new Date(a.created_at) - new Date(b.created_at)
      ));
    }
  };

  // ── Dropdown outside click ───────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (plusButtonRef.current && !plusButtonRef.current.contains(e.target) &&
          dropdownRef.current  && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // ── Access check ─────────────────────────────────────────
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const res  = await fetch(`https://mykaddle-api.onrender.com/api/groups/access/${subjectId}`, { headers: authHeaders });
        const data = await res.json();
        setAccessStatus(data);
      } catch (err) { console.error('Access check failed', err); }
    };
    checkAccess();
  }, [subjectId]);

  // ── File upload ──────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadToast({ message: `Uploading "${file.name}"…`, type: 'uploading' });
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`https://mykaddle-api.onrender.com/api/groups/${subjectId}/upload`, {
        method: 'POST', headers: authHeaders, body: formData
      });
      if (!res.ok) throw new Error('Upload failed');
      const newFile = await res.json();
      setFileList(prev => [newFile, ...prev]);
      setUploadToast({ message: `"${file.name}" uploaded successfully!`, type: 'success' });
      setTimeout(() => setUploadToast(null), 3000);
    } catch (err) {
      console.error('Upload failed', err);
      setUploadToast({ message: `Failed to upload "${file.name}". Please try again.`, type: 'error' });
      setTimeout(() => setUploadToast(null), 3000);
    } finally {
      setUploading(false);
    }
    e.target.value = '';
  };

  // ── Leave group ──────────────────────────────────────────
  const handleLeaveGroup = async () => {
    try {
      await fetch(`https://mykaddle-api.onrender.com/api/groups/leave/${subjectId}`, {
        method: 'POST', headers: authHeaders
      });
      setShowLeaveModal(false);
      navigate(-1);
    } catch (err) {
      console.error('Failed to leave group', err);
    }
  };

  // ── Loading ──────────────────────────────────────────────
  if (accessStatus === null) {
    return (
      <div className="sp-container" style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh' }}>
        <Loader size="8em" />
      </div>
    );
  }

  // ── Trial expired screen ─────────────────────────────────
  if (!accessStatus.access && accessStatus.reason === 'trial_expired') {
    return (
      <div className="sp-container">
        <div className="sp-wrapper">
          {/* Top Bar with Back Button */}
          <div className="sp-top-bar">
            <div className="sp-top-left-header">
              <div className="sp-header-row">
                <button className="sp-back-icon-btn" aria-label="Go back" onClick={() => navigate(-1)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="0.5em" height="1em" viewBox="0 0 12 24">
                    <path d="M0 0h12v24H0z" fill="none" />
                    <path fill="currentColor" fillRule="evenodd" d="M10 19.438L8.955 20.5l-7.666-7.79a1.02 1.02 0 0 1 0-1.42L8.955 3.5L10 4.563L2.682 12z" />
                  </svg>
                </button>
                <span className="sp-page-title">Go to Dashboard</span>
              </div>
            </div>
          </div>

          {/* Centered Content */}
          <div className="sp-blocked-content">
            {/* Lock Icon */}
            <div className="sp-blocked-icon-ring">
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                <path d="M0 0h24v24H0z" fill="none" />
                <g fill="none">
                  <path fill="currentColor" fillOpacity=".25" d="M4 12c0-.943 0-1.414.293-1.707S5.057 10 6 10h12c.943 0 1.414 0 1.707.293S20 11.057 20 12v6.038c0 .38 0 .571-.029.74a2 2 0 0 1-1.164 1.49c-.156.07-.341.116-.71.208c-1.238.31-1.857.464-2.476.578c-2.394.44-4.848.44-7.243 0c-.618-.114-1.237-.269-2.474-.578c-.37-.092-.555-.139-.71-.207a2 2 0 0 1-1.165-1.492C4 18.61 4 18.42 4 18.037z" />
                  <path stroke="currentColor" d="M16.5 10V9a4.5 4.5 0 1 0-9 0v1" />
                  <circle cx="12" cy="15" r="2" fill="currentColor" />
                  <path stroke="currentColor" strokeLinecap="round" d="M12 16v2.5" />
                </g>
              </svg>
            </div>

            {/* Title */}
            <div className="sp-blocked-title">Access Blocked</div>

            {/* Message */}
            <p className="sp-blocked-message">
              Your subscription has ended or your free trial for this group has come to a stop. Choose what you'd like to do below. If something doesn't feel right about this, <strong>contact support</strong>.
            </p>

            {/* Action Buttons */}
            <div className="sp-blocked-actions">
              <button className="sp-btn-leave" onClick={() => setShowLeaveModal(true)}>Leave Group</button>
              <button className="sp-btn-pay" onClick={() => navigate('/dashboard/subscriptions')}>Pay to Stay</button>
            </div>

            {/* Support Link */}
            <span className="sp-support-link" onClick={() => navigate('/dashboard/support')}>Contact Support</span>
          </div>

          {/* Leave Confirmation Modal */}
          {showLeaveModal && (
            <div className="sp-modal-overlay sp-modal-open" onClick={() => setShowLeaveModal(false)}>
              <div className="sp-modal" onClick={e => e.stopPropagation()}>
                <button className="sp-modal-close" onClick={() => setShowLeaveModal(false)}>✕</button>

                <div className="sp-modal-icon-ring">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path fill="currentColor" d="M5.616 20q-.691 0-1.153-.462T4 18.384V15.27h1v3.115q0 .231.192.424t.423.192h12.77q.23 0 .423-.192t.192-.424V5.616q0-.231-.192-.424T18.384 5H5.616q-.231 0-.424.192T5 5.616V8.73H4V5.616q0-.691.463-1.153T5.616 4h12.769q.69 0 1.153.463T20 5.616v12.769q0 .69-.462 1.153T18.384 20zm5.23-4.077l-.707-.72l2.703-2.703H4v-1h8.842L10.14 8.796l.707-.719L14.77 12z" />
                  </svg>
                </div>

                <div className="sp-modal-title">Leave this group?</div>

                <p className="sp-modal-message">
                  Are you sure you want to leave this group? You can always rejoin the same group from Discover, but please note — you won't get another free trial for the same group.
                </p>

                <div className="sp-modal-actions">
                  <button className="sp-btn-cancel" onClick={() => setShowLeaveModal(false)}>Cancel</button>
                  <button className="sp-btn-confirm" onClick={handleLeaveGroup}>Yes, leave group</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const toggleDropdown = (e) => { e.stopPropagation(); setDropdownOpen(!dropdownOpen); };

  const handlePhotosOption = () => {
    fileInputRef.current.accept = 'image/*';
    fileInputRef.current.click();
    setDropdownOpen(false);
  };

  const handleFilesOption = () => {
    fileInputRef.current.accept = '*/*';
    fileInputRef.current.click();
    setDropdownOpen(false);
  };

  const renderFileList = () => {
    if (!Array.isArray(fileList) || fileList.length === 0) {
      return <div className="sp-empty-files">No files uploaded yet.</div>;
    }
    return fileList.map((file) => (
      <div
        key={file._id}
        className="sp-file-item"
        onClick={() => openFile({ url: file.fileUrl, fileId: file._id, subjectId })}
        style={{ cursor:'pointer' }}
      >
        <div className="sp-file-item-top">
          <span className="sp-file-name">{file.fileName}</span>
          {file.createdAt && <ExpiryDot createdAt={file.createdAt} />}
        </div>
        <div className="sp-file-meta">
          <span className="sp-uploader-name">{file.uploaderName}</span>
        </div>
      </div>
    ));
  };

  const hasMessages = Array.isArray(messages) && messages.length > 0;

  return (
    <div className="sp-container">
      <div className="sp-wrapper">
        <div className="sp-portal-body">
          {/* Messenger Container */}
          <div className="sp-messenger-container">
            {/* Sidebar - Participants */}
            <div className="sp-messenger-sidebar">
              <div className="sp-sidebar-header"><h3>Participants</h3></div>
              <div className="sp-participants-list">
                {participants.length === 0 ? (
                  <div className="sp-participant">
                    <div className="sp-participant-name">Fetching</div>
                  </div>
                ) : (
                  participants.map(member => (
                    <div key={member.id} className="sp-participant">
                      <div className="sp-participant-name">
                        <span className={`sp-status-dot ${onlineUsers.includes(member.userId) ? 'online' : 'offline'}`}></span>
                        {member.displayName}
                        {member.role === 'tutor' && (
                          <span className="sp-tutor-badge">(Tutor)</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Chat Main */}
            <div className="sp-chat-main">
              <div className="sp-chat-header">
                <div className="sp-chat-header-row">
                  <button className="sp-chat-back-btn" aria-label="Go back" onClick={() => navigate(-1)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="0.5em" height="1em" viewBox="0 0 12 24">
                      <path d="M0 0h12v24H0z" fill="none" />
                      <path fill="currentColor" fillRule="evenodd" d="M10 19.438L8.955 20.5l-7.666-7.79a1.02 1.02 0 0 1 0-1.42L8.955 3.5L10 4.563L2.682 12z" />
                    </svg>
                  </button>
                  <span className="sp-chat-title">{subjectName || 'Loading...'}</span>
                </div>
              </div>

              <div className="sp-messenger-body">
                {!hasMessages ? (
                  <div className="sp-empty-chat">
                    <div className="sp-empty-chat-icon-ring">
                      <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                        <path d="M0 0h24v24H0z" fill="none" />
                        <g fill="none">
                          <path fill="currentColor" fillOpacity=".25" fillRule="evenodd" d="M19.326 5.778C20 6.787 20 8.19 20 11s0 4.213-.674 5.222a4 4 0 0 1-1.104 1.104c-.881.589-2.064.663-4.222.673V18l-1.106 2.211a1 1 0 0 1-1.788 0L10 18v-.001c-2.158-.01-3.34-.084-4.222-.673a4 4 0 0 1-1.104-1.104C4 15.213 4 13.81 4 11s0-4.213.674-5.222a4 4 0 0 1 1.104-1.104C6.787 4 8.19 4 11 4h2c2.809 0 4.213 0 5.222.674a4 4 0 0 1 1.104 1.104" clipRule="evenodd" />
                          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M8.5 9.5h7m-7 3h5" />
                        </g>
                      </svg>
                    </div>
                    <div className="sp-empty-chat-text">Start Conversation</div>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isMine = msg.sender_id === currentUserId;
                    return (
                      <div key={msg.id} className={`sp-message ${isMine ? 'sent' : 'received'}`}>
                        <div className="sp-sender">{isMine ? 'You' : msg.sender_name}</div>
                        <div className="sp-bubble">{msg.content}</div>
                        <div className="sp-timestamp">
                          {isMine && (
                            <button className="sp-delete-btn" onClick={() => handleDeleteMessage(msg)} title="Delete message">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                                <path fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.5"
                                  d="m19.5 5.5l-.62 10.025c-.158 2.561-.237 3.842-.88 4.763a4 4 0 0 1-1.2 1.128c-.957.584-2.24.584-4.806.584c-2.57 0-3.855 0-4.814-.585a4 4 0 0 1-1.2-1.13c-.642-.922-.72-2.205-.874-4.77L4.5 5.5M3 5.5h18m-4.944 0l-.683-1.408c-.453-.936-.68-1.403-1.071-1.695a2 2 0 0 0-.275-.172C13.594 2 13.074 2 12.035 2c-1.066 0-1.599 0-2.04.234a2 2 0 0 0-.278.18c-.395.303-.616.788-1.058 1.757L8.053 5.5m1.447 11v-6m5 6v-6"/>
                              </svg>
                            </button>
                          )}
                          {formatMessageTime(msg.created_at)}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="sp-messenger-footer">
                <div className="sp-message-input-container">
                  <textarea
                    ref={textareaRef}
                    className="sp-message-input"
                    placeholder="Type a message..."
                    rows="1"
                    value={newMessage}
                    onChange={handleTextareaChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <button className="sp-send-button" onClick={handleSendMessage}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24">
                    <path fill="currentColor" d="m20.34 9.32l-14-7a3 3 0 0 0-4.08 3.9l2.4 5.37a1.06 1.06 0 0 1 0 .82l-2.4 5.37A3 3 0 0 0 5 22a3.14 3.14 0 0 0 1.35-.32l14-7a3 3 0 0 0 0-5.36Zm-.89 3.57l-14 7a1 1 0 0 1-1.35-1.3l2.39-5.37a2 2 0 0 0 .08-.22h6.89a1 1 0 0 0 0-2H6.57a2 2 0 0 0-.08-.22L4.1 5.41a1 1 0 0 1 1.35-1.3l14 7a1 1 0 0 1 0 1.78"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Files Panel */}
          <div className="sp-right-bars">
            <div className="sp-files">
              <div className="sp-files-header">
                <span className="sp-files-title">Group files</span>
                <button className="sp-plus-button" ref={plusButtonRef} onClick={toggleDropdown}>+</button>
              </div>
              <div className="sp-file-list">
                {renderFileList()}
              </div>
              <div className={`sp-dropdown-menu ${dropdownOpen ? 'show' : ''}`} ref={dropdownRef}>
                <div className="sp-menu-item" onClick={handlePhotosOption}><span>Photos</span></div>
                <div className="sp-menu-divider"></div>
                <div className="sp-menu-item" onClick={handleFilesOption}><span>Files</span></div>
              </div>
            </div>
          </div>

          <input type="file" ref={fileInputRef} style={{ display:'none' }} onChange={handleFileUpload} />
        </div>
      </div>

      {uploadToast && (
        <Toast message={uploadToast.message} type={uploadToast.type} onClose={() => setUploadToast(null)} />
      )}

      {viewerFile && (
        <FileViewerModal
          url={viewerFile.url}
          fileId={viewerFile.fileId}
          subjectId={viewerFile.subjectId}
          onClose={closeFile}
        />
      )}
    </div>
  );
};

export default SubjectPortal;